import { ChangeEvent, useEffect, useMemo, useState } from "react"
import { displayStoreWithStock, LatLng, StoreDetails } from "../types/Store";
import { useStoresByUniqueIds, useStoreStockByLocation } from "./StoreLocator";
import { useSettings } from "../Settings";
import { useSapStoreStock } from "./SapInventory";
import { findStoreAttributeValueByName } from "../utils/StoreStock";
import { filterStoresWithStockAvailability } from "../utils/storeUtil";
import { getStoreFeature } from "@/utils/storeDetails";
import { StoreStockSapInventory } from "integration/generated/customTransactions/data-contracts";



export const useClickAndCollect = (partNumbers:string[]) => {
    const {settings} = useSettings();
    const [latLng, setLatLng] = useState<LatLng>({} as LatLng);
    const [storeNumbers, setStoreNumbers] = useState<string[]>([]);
    const [storeUniqueIds, setStoreUniqueIds] = useState<string[] | null>(null);
    const [stores, setStores] = useState<StoreDetails[]>([]);
    const [selectedStoreCC, setSelectedStoreCC] = useState<displayStoreWithStock | undefined>();
    const [displayStores, setDisplayStores] = useState<displayStoreWithStock[]>([]);
    const [storeStock, setStoreStock] = useState<StoreStockSapInventory>();
    const [showSearchByLocation, setShowSearchByLocation] = useState(false);
    const [showSearchNearStores, setShowSearchNearStores] = useState(false);
    const autoCompleteOptions = {
        radius:settings.userData.StoreLocator_Radius,
        radiusUOM:settings.userData.StoreLocator_RadiusUOM,
        maxItems:'25',
    }

    const { stores:tempStores, isLoading } = useStoreStockByLocation(latLng, autoCompleteOptions);
    const {stores:storesByIds} = useStoresByUniqueIds(storeUniqueIds??[]);

    useEffect(()=>{
        if(tempStores?.length>0){
            setStores(tempStores);
            setLatLng({} as LatLng);
        }
    },[tempStores])

    const {
        allowStorePickForOOSItems,
        storepickAllowedAttribute,
        storepickAllowedAttributeValue,
        clickAndCollectShowStoresCount,
        storeEndTimeCutOff
    } = useMemo(()=>{
        return {
            allowStorePickForOOSItems: settings.userData.AllowStorePickForOOSItems,
            storepickAllowedAttribute: settings.userData.StorepickAllowedAttribute,
            storepickAllowedAttributeValue: settings.userData.StorepickAllowedAttributeValue,
            storeEndTimeCutOff: settings.userData.ClickAndCollectStoreEndTimeCutOff,
            clickAndCollectShowStoresCount: settings.userData.ClickAndCollectShowStoresCount?
                                                    parseInt(settings.userData.ClickAndCollectShowStoresCount)
                                                    :[...stores].length
        }
    },[settings,stores]);

    const { sapStoreStock  } = useSapStoreStock(storeNumbers.join(","), partNumbers.join(","), false);
    
    useEffect(()=>{
        if(sapStoreStock?.inventory)setStoreStock(sapStoreStock)
    },[sapStoreStock]);

    useEffect(() => {
        if (stores?.length > 0 && sapStoreStock?.inventory) {
            const inventory = sapStoreStock?.inventory ?? [];
            const currentDay = parseInt(sapStoreStock?.day ?? '0');
            let tempStores = filterStoresWithStockAvailability(
                [...stores], inventory, clickAndCollectShowStoresCount, allowStorePickForOOSItems
            )
            let tempDisplayStores = tempStores?.map((store) => {
                return {
                    store,
                    stock: inventory?.find(iv => iv.storeNumber === store.physicalStoreName)?.available,
                    feature: getStoreFeature(store, currentDay, storeEndTimeCutOff)
                }
            })
            setDisplayStores(tempDisplayStores);
            setStoreNumbers([])
        }
    }, [sapStoreStock, stores])

    useEffect(()=>{
        if(storesByIds?.length>0 && sapStoreStock?.inventory && sapStoreStock?.inventory?.length>0){
            const tempStore = storesByIds.at(0);
            const inventory = sapStoreStock?.inventory??[];
            const currentDay = parseInt(sapStoreStock?.day??'0');
            if(tempStore){
                const tempDisplayStore:displayStoreWithStock = {
                    store:tempStore,
                    stock:inventory?.find(iv=>iv.storeNumber===tempStore.physicalStoreName)?.available,
                    feature:getStoreFeature(tempStore, currentDay, storeEndTimeCutOff)
                }
                setSelectedStoreCC(tempDisplayStore);
                setStoreUniqueIds([]);
            }
        }
    },[storesByIds, sapStoreStock])

    useEffect(()=>{
        if(storesByIds?.length>0){
            setStoreNumbers(storesByIds.map(s=>s.physicalStoreName));
            
        }
    },[storesByIds])

    useEffect(()=>{
        if(stores.length>0){
            let tempStoreIds = [...stores].map(store => {
                const tempPAllowedAttrVal = findStoreAttributeValueByName(store.attributes, storepickAllowedAttribute);
                return storepickAllowedAttributeValue === tempPAllowedAttrVal ? store.physicalStoreName : ""
            }).filter(id=>id!=="");
            setStoreNumbers(tempStoreIds);
        }
    },[stores])

    const handleShowNearStoresCheckChange = (event: ChangeEvent<HTMLInputElement>) => {
        let tempShowCCStoreCount = clickAndCollectShowStoresCount;
        let tempStores = [...stores];
        const checked = event?.target?.checked;
        setShowSearchNearStores(checked)
        if (checked) {
            tempShowCCStoreCount = tempStores.length
        }
        if (stores?.length > 0 && storeStock?.inventory) {
            const inventory = storeStock?.inventory ?? [];
            const currentDay = parseInt(storeStock?.day ?? '0');
            let tempStores = filterStoresWithStockAvailability(
                [...stores], inventory, 5, allowStorePickForOOSItems, tempShowCCStoreCount
            )
            let tempDisplayStores = tempStores?.map((store) => {
                return {
                    store,
                    stock: inventory?.find(iv => iv.storeNumber === store.physicalStoreName)?.available,
                    feature: getStoreFeature(store, currentDay, storeEndTimeCutOff)
                }
            })
            setDisplayStores(tempDisplayStores);
        }
    }
    return {
        latLng,
        setLatLng,
        stores,
        setStores,
        setStoreNumbers,
        setStoreUniqueIds,
        selectedStoreCC,
        setSelectedStoreCC,
        displayStores,
        setDisplayStores,
        handleShowNearStoresCheckChange,
        showSearchByLocation,
        setShowSearchByLocation,
        showSearchNearStores,
        setShowSearchNearStores
    }
}